import { useState, useEffect, useRef, useMemo } from 'react';
import {
  X, UploadCloud, ZoomIn, ChevronRight, List, ChevronLeft, Edit2, AlertCircle, CheckCircle
} from 'lucide-react';

import { uploadProjectDocumentation, updateProjectDocumentationTaskName, fetchProjectDocumentation, deleteProjectDocumentation } from '../../../services/projectReportServices';
import MediaModal from './MediaModal';

const mapApiFiles = (apiFiles) => {
  if (!apiFiles || !Array.isArray(apiFiles)) return [];
  return apiFiles
    .filter(f => f.isDeleted !== 'ONE' && f.isDeleted !== 1)
    .map(f => ({
      id: f.id,
      originalUrl: f.imageFilename,
      url: f.imageThumbnail,
      taskName: f.taskName || 'uncategorized',
      createdAt: f.createdAt,
    }));
};

const MediaTab = ({ projectId, tasks, accent, permissions }) => {

  const [files, setFiles] = useState([]);
  const [lightboxData, setLightboxData] = useState(null);
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef(null);
  const thumbStripRef = useRef(null);
  const dragRef = useRef(null);

  const [imgTransform, setImgTransform] = useState({ scale: 1, x: 0, y: 0 });
  const [isDraggingImg, setIsDraggingImg] = useState(false);

  // const isReadOnly = (isEdited === 'false' || isEdited === false) && (onIframe === 'true' || onIframe === true);
  const isReadOnly = !permissions.includes("project_report:edit");

  // Modal State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [selectedTaskName, setSelectedTaskName] = useState('');

  // Edit Specific Image State
  const [editModalData, setEditModalData] = useState(null);
  const [isUpdatingCategory, setIsUpdatingCategory] = useState(false);

  const fetchMediaFiles = async () => {
    const response = await fetchProjectDocumentation(projectId);
    setFiles(mapApiFiles(response));
  };

  useEffect(() => {
    fetchMediaFiles();
  }, [projectId]);

  useEffect(() => {
    setImgTransform({ scale: 1, x: 0, y: 0 });
    setIsDraggingImg(false);
    dragRef.current = null;
    if (!thumbStripRef.current || !lightboxData) return;
    const active = thumbStripRef.current.children[lightboxData.currentIndex];
    active?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
  }, [lightboxData?.currentIndex]);

  const handleImgMouseDown = (e) => {
    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      startTx: imgTransform.x,
      startTy: imgTransform.y,
      moved: false,
      wrapperRect: e.currentTarget.getBoundingClientRect(),
    };
    if (imgTransform.scale > 1) setIsDraggingImg(true);
  };

  const handleImgMouseMove = (e) => {
    if (!dragRef.current || imgTransform.scale <= 1) return;
    const dx = e.clientX - dragRef.current.startX;
    const dy = e.clientY - dragRef.current.startY;
    if (Math.abs(dx) > 3 || Math.abs(dy) > 3) {
      dragRef.current.moved = true;
      // capture values sebelum setState untuk menghindari null ref saat updater berjalan async
      const newX = dragRef.current.startTx + dx;
      const newY = dragRef.current.startTy + dy;
      setImgTransform(prev => ({ ...prev, x: newX, y: newY }));
    }
  };

  const handleImgMouseUp = (e) => {
    if (!dragRef.current) return;
    const { moved, wrapperRect } = dragRef.current;
    dragRef.current = null;
    setIsDraggingImg(false);
    if (!moved) {
      if (imgTransform.scale > 1) {
        setImgTransform({ scale: 1, x: 0, y: 0 });
      } else {
        const S = 2.5;
        const cx = e.clientX - (wrapperRect.left + wrapperRect.width / 2);
        const cy = e.clientY - (wrapperRect.top + wrapperRect.height / 2);
        setImgTransform({ scale: S, x: cx * (1 - S), y: cy * (1 - S) });
      }
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const dropped = Array.from(e.dataTransfer.files).filter(f => f.type.startsWith('image/'));
    if (dropped.length > 0) {
      const newPending = dropped.map(file => ({
        url: URL.createObjectURL(file),
        file
      }));
      setPendingFiles(newPending);
      setIsModalOpen(true);
    }
  };

  const handleFile = (e) => {
    const chosen = Array.from(e.target.files).filter(f => f.type.startsWith('image/'));
    if (chosen.length > 0) {
      const newPending = chosen.map(file => ({
        url: URL.createObjectURL(file),
        file
      }));
      setPendingFiles(newPending);
      setIsModalOpen(true);
    }
  };

  const cancelUpload = () => {
    setPendingFiles([]);
    setSelectedTaskName('');
    setIsModalOpen(false);
    if (inputRef.current) inputRef.current.value = '';
  };

  const [isUploading, setIsUploading] = useState(false);
  const [notification, setNotification] = useState(null);

  const showNotification = (message, type = 'error') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 4000);
  };

  const getCurrentTimeFormatted = () => {
    const now = new Date();
    const YYYY = now.getFullYear();
    const MM = String(now.getMonth() + 1).padStart(2, '0');
    const DD = String(now.getDate()).padStart(2, '0');
    const H = String(now.getHours()).padStart(2, '0');
    const m = String(now.getMinutes()).padStart(2, '0');
    const s = String(now.getSeconds()).padStart(2, '0');
    const suffix = Math.random().toString(36).slice(2, 6);
    return `${YYYY}-${MM}-${DD}T${H}:${m}:${s}-${suffix}`;
  };

  const confirmUpload = async () => {
    if (pendingFiles.length === 0) return;
    setIsUploading(true);

    try {
      const tName = selectedTaskName || 'uncategorized';

      for (const pf of pendingFiles) {
        const formData = new FormData();
        formData.append('id_project', projectId);
        formData.append('current_time', getCurrentTimeFormatted());
        formData.append('taskName', tName);
        formData.append('file', pf.file);
        await uploadProjectDocumentation(formData);
      }

      // Refresh list foto dari API setelah upload berhasil
      await fetchMediaFiles();

      cancelUpload();
    } catch (error) {
      showNotification("Gagal Upload, Pastikan format file sesuai", "error");
    } finally {
      setIsUploading(false);
    }
  };

  const confirmDelete = async (documentationId) => {
    if (!documentationId) return;
    try {
      await deleteProjectDocumentation(documentationId);
      await fetchMediaFiles();
      setEditModalData(null);
      showNotification("Foto berhasil dihapus", "success");
    } catch (error) {
      showNotification("Gagal Menghapus, Coba Lagi Nanti", "error");
    }
  };

  const { groupedMedia, groupKeys } = useMemo(() => {
    const grouped = {};
    files.forEach(f => {
      const key = f.taskName || 'uncategorized';
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(f);
    });
    const keys = Object.keys(grouped).sort((a, b) => {
      if (a === 'uncategorized') return 1;
      if (b === 'uncategorized') return -1;
      return a.localeCompare(b);
    });
    return { groupedMedia: grouped, groupKeys: keys };
  }, [files]);

  const formatGroupLabel = (key) => key === 'uncategorized' ? 'Uncategorized' : key;

  return (
    <div>
      {/* Gallery header */}
      <div style={{ textAlign: 'center', padding: '24px', borderRadius: '16px', background: 'linear-gradient(135deg, #f8fafc, #f1f5f9)', marginBottom: '20px', border: '1px solid #e2e8f0' }}>
        <h3 style={{ margin: '0 0 4px', fontSize: '18px', fontWeight: '800', color: '#0f172a' }}>Project Gallery</h3>
        <p style={{ margin: 0, fontSize: '12.5px', color: '#94a3b8' }}>Upload and view project documentation images</p>
      </div>

      {/* Upload Zone */}
      {!isReadOnly && (
        <div style={{ marginBottom: '24px' }}>
          <div
            onDragEnter={(e) => { e.preventDefault(); setDragging(true); }}
            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={handleDrop}
            onClick={() => inputRef.current?.click()}
            style={{
              height: '140px',
              borderRadius: '14px',
              border: dragging ? `2px solid ${accent}` : '2px dashed #cbd5e1',
              background: dragging ? `${accent}08` : '#fafafa',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              cursor: 'pointer',
              transition: 'all 0.2s',
              maxWidth: '300px',
              margin: '0 auto'
            }}
          >
            <div style={{ width: '44px', height: '44px', borderRadius: '12px', background: `${accent}15`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <UploadCloud size={22} color={accent} />
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '12px', fontWeight: '600', color: '#374151' }}>Drag & Drop</div>
              <div style={{ fontSize: '11px', color: accent, fontWeight: '600', marginTop: '3px' }}>or Choose File</div>
            </div>
            <input ref={inputRef} type="file" multiple accept="image/*" style={{ display: 'none' }} onChange={handleFile} />
          </div>
        </div>
      )}


      {/* Grouped Galleries */}
      {groupKeys.length === 0 && (
        <div style={{ textAlign: 'center', padding: '40px', color: '#94a3b8', fontSize: '14px' }}>
          Belum ada foto yang diunggah.
        </div>
      )}

      {groupKeys.map(groupKey => {
        const groupName = formatGroupLabel(groupKey);
        const groupImages = groupedMedia[groupKey];

        return (
          <div key={groupKey} style={{ marginBottom: '32px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px', paddingBottom: '8px', borderBottom: '1px solid #f1f5f9' }}>
              <List size={16} color={accent} />
              <h4 style={{ margin: 0, fontSize: '15px', fontWeight: '700', color: '#334155' }}>
                {groupName} <span style={{ fontSize: '12px', color: '#94a3b8', fontWeight: '500', marginLeft: '6px' }}>({groupImages.length})</span>
              </h4>
            </div>

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
                gap: '14px',
              }}
            >
              {groupImages.map((imgObj, idx) => (
                <div
                  key={imgObj.id || idx}
                  style={{
                    height: '180px',
                    borderRadius: '14px',
                    overflow: 'hidden',
                    position: 'relative',
                    cursor: 'pointer',
                    boxShadow: '0 3px 10px rgba(0,0,0,0.08)',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.02)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.15)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = '0 3px 10px rgba(0,0,0,0.08)'; }}
                  onClick={() => setLightboxData({ images: groupImages, currentIndex: idx })}
                >
                  <img src={`${import.meta.env.VITE_API_BACKEND_PROJECT_REPORT}${imgObj.url}`} alt={`media-${idx}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  {/* Hover overlay */}
                  <div
                    style={{
                      position: 'absolute', inset: 0,
                      background: 'rgba(0,0,0,0)',
                      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '8px',
                      transition: 'background 0.2s',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'rgba(0,0,0,0.45)';
                      Array.from(e.currentTarget.querySelectorAll('.hover-action-btn')).forEach(el => el.style.opacity = '1');
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'rgba(0,0,0,0)';
                      Array.from(e.currentTarget.querySelectorAll('.hover-action-btn')).forEach(el => el.style.opacity = '0');
                    }}
                  >
                    <div
                      className="hover-action-btn"
                      style={{
                        display: 'flex', alignItems: 'center', gap: '6px', opacity: 0, transition: 'opacity 0.2s', background: 'rgba(255,255,255,0.2)', padding: '6px 14px', borderRadius: '20px', backdropFilter: 'blur(4px)'
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.3)'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.2)'; }}
                      onClick={(e) => { e.stopPropagation(); setLightboxData({ images: groupImages, currentIndex: idx }); }}
                    >
                      <ZoomIn size={16} color="#fff" />
                      <span style={{ color: '#fff', fontSize: '12px', fontWeight: '500' }}>View</span>
                    </div>
                    {!isReadOnly && (
                      <div
                        className="hover-action-btn"
                        style={{
                          display: 'flex', alignItems: 'center', gap: '6px', opacity: 0, transition: 'opacity 0.2s', background: 'rgba(255,255,255,0.2)', padding: '6px 14px', borderRadius: '20px', backdropFilter: 'blur(4px)'
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.3)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.2)'; }}
                        onClick={(e) => { e.stopPropagation(); setEditModalData(imgObj); }}
                      >
                        <Edit2 size={16} color="#fff" />
                        <span style={{ color: '#fff', fontSize: '12px', fontWeight: '500' }}>Edit</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}

      {/* Edit Category Modal */}
      {editModalData && (
        <MediaModal
          mode="edit"
          accent={accent}
          tasks={tasks}
          imageUrl={`${import.meta.env.VITE_API_BACKEND_PROJECT_REPORT}${editModalData.url}`}
          taskValue={editModalData.taskName === 'uncategorized' ? '' : editModalData.taskName}
          onTaskChange={(val) => setEditModalData({ ...editModalData, taskName: val })}
          onClose={() => setEditModalData(null)}
          onConfirm={async () => {
            try {
              setIsUpdatingCategory(true);
              const tName = editModalData.taskName || 'uncategorized';
              await updateProjectDocumentationTaskName({ id: editModalData.id, projectId, taskName: tName });
              await fetchMediaFiles();
              setEditModalData(null);
            } catch {
              showNotification("Gagal memperbarui kategori foto", "error");
            } finally {
              setIsUpdatingCategory(false);
            }
          }}
          isLoading={isUpdatingCategory}
          onDelete={() => confirmDelete(editModalData.id)}
        />
      )}

      {/* Upload Modal */}
      {isModalOpen && (
        <MediaModal
          mode="upload"
          accent={accent}
          tasks={tasks}
          pendingFiles={pendingFiles}
          taskValue={selectedTaskName}
          onTaskChange={(val) => setSelectedTaskName(val)}
          onClose={cancelUpload}
          onConfirm={confirmUpload}
          isLoading={isUploading}
        />
      )}

      {/* Lightbox */}
      {lightboxData && lightboxData.images[lightboxData.currentIndex] && (() => {
        const { images, currentIndex } = lightboxData;
        const navBtnBase = { width: '48px', height: '48px', borderRadius: '50%', background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.2)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(8px)', transition: 'all 0.2s', flexShrink: 0 };
        return (
          <>
            {/* Backdrop */}
            <div onClick={() => setLightboxData(null)} style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(6px)' }} />

            {/* Container */}
            <div
              onMouseMove={handleImgMouseMove}
              onMouseUp={handleImgMouseUp}
              style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', zIndex: 1001, width: '90vw', maxHeight: '92vh', display: 'flex', flexDirection: 'column', gap: '12px', padding: '48px 16px 16px' }}
            >

              {/* Close */}
              <button
                onClick={() => setLightboxData(null)}
                style={{ position: 'absolute', top: '12px', right: '12px', width: '40px', height: '40px', borderRadius: '50%', background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.2)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(8px)', transition: 'all 0.2s', zIndex: 1002 }}
                onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.25)'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.15)'}
              >
                <X size={20} color="#fff" />
              </button>

              {/* Main image row */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', height: 'calc(92vh - 200px)' }}>
                <button
                  onClick={() => setLightboxData(prev => ({ ...prev, currentIndex: prev.currentIndex - 1 }))}
                  disabled={currentIndex === 0}
                  style={{ ...navBtnBase, opacity: currentIndex === 0 ? 0.3 : 1 }}
                  onMouseEnter={(e) => { if (currentIndex > 0) e.currentTarget.style.background = 'rgba(255,255,255,0.25)'; }}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.15)'}
                >
                  <ChevronLeft size={24} color="#fff" />
                </button>

                <div
                  onMouseDown={handleImgMouseDown}
                  style={{ flex: 1, minWidth: 0, height: '100%', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: imgTransform.scale > 1 ? (isDraggingImg ? 'grabbing' : 'grab') : 'zoom-in' }}
                >
                  <img
                    src={images[currentIndex].originalUrl}
                    alt="lightbox"
                    draggable={false}
                    style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', transform: `translate(${imgTransform.x}px, ${imgTransform.y}px) scale(${imgTransform.scale})`, transition: isDraggingImg ? 'none' : 'transform 0.25s ease', userSelect: 'none', pointerEvents: 'none', filter: 'drop-shadow(0 20px 40px rgba(0,0,0,0.4))' }}
                  />
                </div>

                <button
                  onClick={() => setLightboxData(prev => ({ ...prev, currentIndex: prev.currentIndex + 1 }))}
                  disabled={currentIndex === images.length - 1}
                  style={{ ...navBtnBase, opacity: currentIndex === images.length - 1 ? 0.3 : 1 }}
                  onMouseEnter={(e) => { if (currentIndex < images.length - 1) e.currentTarget.style.background = 'rgba(255,255,255,0.25)'; }}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.15)'}
                >
                  <ChevronRight size={24} color="#fff" />
                </button>
              </div>

              {/* Thumbnail strip */}
              <div ref={thumbStripRef} style={{ display: 'flex', justifyContent: 'center', overflowX: 'auto', overflowY: 'hidden', gap: '6px', padding: '4px 2px 12px', scrollBehavior: 'smooth' }}>
                {images.map((img, idx) => (
                  <img
                    key={img.id}
                    src={`${import.meta.env.VITE_API_BACKEND_PROJECT_REPORT}${img.url}`}
                    alt={`thumb-${idx}`}
                    onClick={() => setLightboxData(prev => ({ ...prev, currentIndex: idx }))}
                    style={{ width: '80px', height: '64px', objectFit: 'cover', borderRadius: '6px', cursor: 'pointer', flexShrink: 0, opacity: idx === currentIndex ? 1 : 0.45, border: idx === currentIndex ? '2px solid #fff' : '2px solid transparent', transition: 'opacity 0.2s, border-color 0.2s' }}
                  />
                ))}
              </div>

              {/* Counter */}
              <div style={{ textAlign: 'center', color: '#fff', opacity: 0.7, fontSize: '13px' }}>
                {currentIndex + 1} / {images.length}
              </div>
            </div>
          </>
        );
      })()}

      {/* Toast Notification */}
      {notification && (
        <div style={{
          position: 'fixed', top: '32px', right: '32px', zIndex: 9999,
          background: notification.type === 'error' ? '#fee2e2' : '#dcfce7',
          borderLeft: `4px solid ${notification.type === 'error' ? '#ef4444' : '#22c55e'}`,
          borderRadius: '8px', padding: '16px 20px', display: 'flex', alignItems: 'center', gap: '12px',
          boxShadow: '0 10px 25px rgba(0,0,0,0.1)', animation: 'fadeSlideUp 0.3s ease'
        }}>
          {notification.type === 'error' ? (
            <AlertCircle size={20} color="#ef4444" />
          ) : (
            <CheckCircle size={20} color="#22c55e" />
          )}
          <div style={{ flex: 1 }}>
            <span style={{
              color: notification.type === 'error' ? '#991b1b' : '#166534',
              fontSize: '14px', fontWeight: '600', display: 'block'
            }}>
              {notification.message}
            </span>
          </div>
          <button onClick={() => setNotification(null)} style={{ background: 'none', border: 'none', padding: 0, marginLeft: '8px', cursor: 'pointer', display: 'flex', opacity: 0.7 }}>
            <X size={16} color={notification.type === 'error' ? '#991b1b' : '#166534'} />
          </button>
        </div>
      )}
    </div>
  );
};

export default MediaTab;
